import { z } from 'zod';
import { IdSchema, ResultIdSchema, TenantIdSchema } from './common.js';
import { QueryPlanSchema } from './records.js';
import { FeedbackKindSchema } from './tools.js';

/*
 * Conversas (ADR 0039): uma linha por pergunta respondida por um canal com IA própria (o bot do Teams).
 * Guarda a pergunta, o texto da resposta e os passos da LLM. Valores de resultado não entram: eles
 * ficam no repositório de resultados, pelo result_id, e com privacy_mode nem o texto da resposta os traz.
 */

/** Uma chamada de ferramenta do turno, com os argumentos como a LLM mandou. */
export const TurnStepSchema = z.object({
  tool: z.string().min(1).max(64),
  input: z.record(z.string(), z.unknown()),
  status: z.enum(['ok', 'error']),
  error_code: z.string().max(64).nullable(),
  result_id: ResultIdSchema.nullable(),
  ms: z.number().int().min(0),
});
export type TurnStep = z.infer<typeof TurnStepSchema>;

/**
 * Tokens da LLM no turno inteiro (agente, recibo, classificador e sugestões), como o provedor contou.
 * cached é a parte da entrada que veio do cache do provedor, cobrada mais barato. Base para medir o custo por pergunta.
 */
export const TurnTokensSchema = z.object({
  input: z.number().int().min(0),
  cached: z.number().int().min(0),
  output: z.number().int().min(0),
  calls: z.number().int().min(0),
});
export type TurnTokens = z.infer<typeof TurnTokensSchema>;

/** O que o canal manda ao fim do turno. Quem perguntou, tenant e canal vêm do token, nunca daqui. */
export const TurnPostSchema = z.object({
  /** Id da conversa no canal, já com hash: agrupa os turnos sem guardar o id do Teams. */
  conversation: z.string().min(1).max(128),
  question: z.string().max(4000),
  answer: z.string().max(20_000),
  model_id: z.string().max(200),
  steps: z.array(TurnStepSchema).max(50),
  llm_rounds: z.number().int().min(0).max(100),
  /** Opcional: bots anteriores à 0.4.4 não mandam. */
  tokens: TurnTokensSchema.optional(),
  total_ms: z.number().int().min(0),
  /** Números que a LLM escreveu sem fonte; com algum, a resposta foi trocada pelo texto do Lastrouter. */
  unverified_numbers: z.number().int().min(0),
  /** Resultados mostrados à pessoa no cartão. */
  result_ids: z.array(ResultIdSchema).max(20),
  /** Código curto do erro, quando o turno falhou (nunca a mensagem crua). */
  error: z.string().max(200).nullable(),
});
export type TurnPost = z.infer<typeof TurnPostSchema>;

export const TurnRecordSchema = TurnPostSchema.extend({
  id: z.string(),
  ts: z.string(),
  tenant_id: TenantIdSchema,
  user: z.string(),
  user_subject: z.string(),
  channel: z.string(),
  /** Assuntos dos resultados do turno, conferidos no repositório de resultados. */
  subject_ids: z.array(IdSchema),
});
export type TurnRecord = z.infer<typeof TurnRecordSchema>;

// ---------- painel ----------

export const ConversationFeedbackSchema = z.object({ id: z.string(), ts: z.string(), kind: FeedbackKindSchema, comment: z.string().nullable(), user: z.string() });

/**
 * Uma conversa na tela: um turno gravado pelo canal (kind turn) ou, nos canais sem bot próprio
 * (Claude.ai, Copilot), as linhas de auditoria agrupadas pela pergunta (kind audit).
 */
export const ConversationSchema = z.object({
  id: z.string(),
  kind: z.enum(['turn', 'audit']),
  ts: z.string(),
  user: z.string(),
  channel: z.string(),
  conversation: z.string().nullable(),
  question: z.string().nullable(),
  /** Só nos turnos: os canais sem bot não mandam o texto da resposta. */
  answer: z.string().nullable(),
  model_id: z.string().nullable(),
  status: z.enum(['ok', 'error']),
  error: z.string().nullable(),
  duration_ms: z.number().int(),
  llm_rounds: z.number().int().nullable(),
  tokens: TurnTokensSchema.nullable(),
  unverified_numbers: z.number().int().nullable(),
  steps: z.array(TurnStepSchema),
  subject_ids: z.array(z.string()),
  result_ids: z.array(z.string()),
  /** Do mais novo para o mais antigo. */
  feedback: z.array(ConversationFeedbackSchema),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationQuerySchema = z.object({
  from: z.string().max(40).optional(),
  to: z.string().max(40).optional(),
  user: z.string().max(256).optional(),
  channel: z.string().max(64).optional(),
  subject_id: IdSchema.optional(),
  /** wrong inclui os pedidos de revisão; none: sem retorno. */
  feedback: z.enum(['useful', 'wrong', 'none']).optional(),
  status: z.enum(['ok', 'error']).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});
export type ConversationQuery = z.infer<typeof ConversationQuerySchema>;

/** Metadados de um resultado para o detalhe da conversa: sem linhas nem valores. */
export const ConversationResultSchema = z.object({
  result_id: z.string(),
  subject_id: z.string(),
  app: z.string(),
  query_id: z.string(),
  query_version: z.number().int(),
  certified: z.boolean(),
  measures: z.array(z.string()),
  /** Filtros do plano: valores escolhidos para filtrar, não valores do resultado. */
  filters: QueryPlanSchema.shape.filters,
  caveats: z.array(z.string()),
  row_count: z.number().int(),
  executed_at: z.string(),
});
export type ConversationResult = z.infer<typeof ConversationResultSchema>;

const ConversationFilterSchema = ConversationQuerySchema.omit({ limit: true });

export const CONVERSATIONS_ROUTES = {
  conversations: {
    method: 'GET',
    path: '/conversations',
    access: 'owner',
    summary: 'Perguntas e respostas, com os passos e o retorno (dono vê só as dos seus apps)',
    query: ConversationQuerySchema,
    response: z.object({ items: z.array(ConversationSchema), truncated: z.boolean() }),
  },
  conversationResults: {
    method: 'GET',
    path: '/conversations/results',
    access: 'owner',
    summary: 'Metadados dos resultados de uma conversa (medida, filtros, certificação)',
    query: z.object({ ids: z.string().max(600) }),
    response: z.array(ConversationResultSchema),
  },
  conversationsExport: { method: 'GET', path: '/conversations/export.csv', access: 'admin', summary: 'Conversas filtradas em CSV', query: ConversationFilterSchema },
} as const;
